import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeftRight,
  Bot,
  Building2,
  CreditCard,
  KeyRound,
  ShieldCheck,
  UserCircle,
  Wallet,
} from 'lucide-react';

export interface ScopeEntry {
  key: string;
  label: string;
  description: string;
}

export interface ScopeGroup {
  id: string;
  icon: LucideIcon;
  label: string;
  scopes: ScopeEntry[];
}

export interface UseCasePreset {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  scopes: string[];
}

export const SCOPE_GROUPS: ScopeGroup[] = [
  {
    id: 'identity',
    icon: UserCircle,
    label: 'Identity',
    scopes: [
      {
        key: 'identity.read.self',
        label: 'Read profile',
        description: 'Read your identity and profile information',
      },
      {
        key: 'identity.update.self',
        label: 'Update profile',
        description: 'Modify your profile details',
      },
      {
        key: 'alias.read.self',
        label: 'Read aliases',
        description: 'View your linked aliases',
      },
      {
        key: 'alias.find',
        label: 'Find aliases',
        description: 'Look up aliases',
      },
      {
        key: 'alias.verify',
        label: 'Verify aliases',
        description: 'Verify alias ownership',
      },
      {
        key: 'alias.write.self',
        label: 'Create aliases',
        description: 'Link new aliases to your account',
      },
    ],
  },
  {
    id: 'accounts',
    icon: Wallet,
    label: 'Accounts & Wallets',
    scopes: [
      {
        key: 'accounts.read',
        label: 'Read accounts',
        description: 'View account details',
      },
      {
        key: 'accounts.read.self',
        label: 'Read own accounts',
        description: 'View your own account details',
      },
      {
        key: 'accounts.balance',
        label: 'Check balances',
        description: 'Query account balances',
      },
      {
        key: 'accounts.list',
        label: 'List accounts',
        description: 'List all accessible accounts',
      },
      {
        key: 'accounts.list_transactions',
        label: 'View transactions',
        description: 'Read transaction history',
      },
      {
        key: 'accounts.transfer',
        label: 'Transfer funds',
        description: 'Move funds between accounts',
      },
      {
        key: 'accounts.transfer.self',
        label: 'Transfer own funds',
        description: 'Transfer from your own accounts',
      },
      {
        key: 'accounts.sync.self',
        label: 'Sync accounts',
        description: 'Synchronize account data',
      },
      {
        key: 'accounts.batch',
        label: 'Batch operations',
        description: 'Execute batch account operations',
      },
      {
        key: 'accounts.tokenize',
        label: 'Tokenize',
        description: 'Tokenize account references',
      },
      {
        key: 'accounts.tokenize.self',
        label: 'Tokenize own',
        description: 'Tokenize your own accounts',
      },
      {
        key: 'mediums.read',
        label: 'Read mediums',
        description: 'View payment mediums',
      },
      {
        key: 'mediums.write',
        label: 'Write mediums',
        description: 'Create payment mediums',
      },
      {
        key: 'mediums.write.self',
        label: 'Manage own mediums',
        description: 'Create and manage your payment mediums',
      },
      {
        key: 'mediums.update.self',
        label: 'Update own mediums',
        description: 'Modify your existing mediums',
      },
    ],
  },
  {
    id: 'payments',
    icon: CreditCard,
    label: 'Payments',
    scopes: [
      {
        key: 'payments.write.self',
        label: 'Create own payments',
        description: 'Create payment links for your account',
      },
      {
        key: 'payments.write',
        label: 'Create payments',
        description: 'Create payments on behalf of others',
      },
      {
        key: 'payments.read.self',
        label: 'Read own payments',
        description: 'View your payment history',
      },
      {
        key: 'payments.read',
        label: 'Read payments',
        description: 'View all accessible payments',
      },
      {
        key: 'payments.pay',
        label: 'Execute payments',
        description: 'Complete payment transactions',
      },
      {
        key: 'payments.create',
        label: 'Create payment records',
        description: 'Create new payment entries',
      },
      {
        key: 'payments.list',
        label: 'List payments',
        description: 'List all accessible payments',
      },
    ],
  },
  {
    id: 'swaps',
    icon: ArrowLeftRight,
    label: 'Swaps & Exchange',
    scopes: [
      {
        key: 'swap.take',
        label: 'Execute swaps',
        description: 'Take available swap orders',
      },
      {
        key: 'swap.taker',
        label: 'Taker operations',
        description: 'Operate as swap taker',
      },
      {
        key: 'swap.publish',
        label: 'Publish swaps',
        description: 'Publish swap offers',
      },
      {
        key: 'swap.write.self',
        label: 'Create own swaps',
        description: 'Create swap orders for your account',
      },
      {
        key: 'swap.read',
        label: 'Read swaps',
        description: 'View swap details',
      },
      {
        key: 'swap.read.self',
        label: 'Read own swaps',
        description: 'View your swap history',
      },
      {
        key: 'swap.list',
        label: 'List swaps',
        description: 'List available swaps',
      },
      {
        key: 'swap.maker',
        label: 'Maker operations',
        description: 'Operate as swap maker',
      },
      {
        key: 'swap.find_rate',
        label: 'Query rates',
        description: 'Look up exchange rates',
      },
      {
        key: 'swap.calculate_rates',
        label: 'Calculate rates',
        description: 'Calculate swap rates',
      },
      {
        key: 'swap.market_maker',
        label: 'Market making',
        description: 'Automated market making operations',
      },
      {
        key: 'swap.orders.list',
        label: 'List orders',
        description: 'List swap orders',
      },
      {
        key: 'graph.template.read',
        label: 'Read graph templates',
        description: 'View graph route templates',
      },
      {
        key: 'graph.template.generate',
        label: 'Generate templates',
        description: 'Create graph route templates',
      },
    ],
  },
  {
    id: 'orgs',
    icon: Building2,
    label: 'Organizations',
    scopes: [
      {
        key: 'orgs.read',
        label: 'Read organizations',
        description: 'View organization details',
      },
      {
        key: 'orgs.read.self',
        label: 'Read own org',
        description: 'View your organization',
      },
      {
        key: 'orgs.write',
        label: 'Write organizations',
        description: 'Modify organization settings',
      },
      {
        key: 'orgs.write.self',
        label: 'Write own org',
        description: 'Modify your organization',
      },
      {
        key: 'orgs.create',
        label: 'Create organizations',
        description: 'Create new organizations',
      },
      {
        key: 'orgs.find',
        label: 'Find organizations',
        description: 'Search for organizations',
      },
      {
        key: 'orgs.remove',
        label: 'Remove organizations',
        description: 'Delete organizations',
      },
      {
        key: 'team.read',
        label: 'Read teams',
        description: 'View team membership',
      },
      {
        key: 'team.write',
        label: 'Write teams',
        description: 'Manage team membership',
      },
      {
        key: 'team.write.self',
        label: 'Write own team',
        description: 'Manage your team',
      },
      {
        key: 'teams.read.self',
        label: 'Read own teams',
        description: 'View your teams',
      },
      {
        key: 'teams.write.self',
        label: 'Write own teams',
        description: 'Manage your teams',
      },
      {
        key: 'member.read',
        label: 'Read members',
        description: 'View members',
      },
      {
        key: 'member.write',
        label: 'Write members',
        description: 'Manage members',
      },
      {
        key: 'member.write.self',
        label: 'Write own member',
        description: 'Manage your membership',
      },
      {
        key: 'members.read.self',
        label: 'Read own members',
        description: 'View your members',
      },
      {
        key: 'members.write.self',
        label: 'Write own members',
        description: 'Manage your members',
      },
      { key: 'role.read', label: 'Read roles', description: 'View roles' },
      {
        key: 'role.read.self',
        label: 'Read own roles',
        description: 'View your roles',
      },
      { key: 'role.write', label: 'Write roles', description: 'Manage roles' },
      {
        key: 'role.write.self',
        label: 'Write own roles',
        description: 'Manage your roles',
      },
      {
        key: 'invite.accept',
        label: 'Accept invites',
        description: 'Accept organization invitations',
      },
      {
        key: 'invite.reject',
        label: 'Reject invites',
        description: 'Reject organization invitations',
      },
      {
        key: 'invite.write.self',
        label: 'Send invites',
        description: 'Send organization invitations',
      },
    ],
  },
  {
    id: 'compliance',
    icon: ShieldCheck,
    label: 'Compliance',
    scopes: [
      {
        key: 'compliance.read',
        label: 'Read compliance',
        description: 'View compliance status',
      },
      {
        key: 'compliance.read.documents',
        label: 'Read documents',
        description: 'View compliance documents',
      },
      {
        key: 'compliance.write',
        label: 'Write compliance',
        description: 'Submit compliance data',
      },
      {
        key: 'compliance.start_verification',
        label: 'Start verification',
        description: 'Initiate KYC verification',
      },
    ],
  },
  {
    id: 'agent',
    icon: Bot,
    label: 'AI Agent',
    scopes: [
      {
        key: 'agent.read',
        label: 'Read agents',
        description: 'View agent configuration',
      },
      {
        key: 'agent.write',
        label: 'Write agents',
        description: 'Update agent configuration',
      },
      {
        key: 'chat.read',
        label: 'Read chats',
        description: 'View chat history',
      },
      {
        key: 'chat.write',
        label: 'Write chats',
        description: 'Send messages to agents',
      },
      {
        key: 'file.read',
        label: 'Read files',
        description: 'Access uploaded files',
      },
      {
        key: 'file.write',
        label: 'Upload files',
        description: 'Upload new files',
      },
    ],
  },
  {
    id: 'security',
    icon: KeyRound,
    label: 'Security & Keys',
    scopes: [
      {
        key: 'api-key.list',
        label: 'List API keys',
        description: 'View your API keys',
      },
      {
        key: 'api-key.validate',
        label: 'Validate keys',
        description: 'Verify API key validity',
      },
      {
        key: 'api-key.revoke',
        label: 'Revoke keys',
        description: 'Revoke existing API keys',
      },
      {
        key: 'api-key.rotate',
        label: 'Rotate keys',
        description: 'Rotate API key secrets',
      },
      {
        key: 'signer.get.self',
        label: 'Get signers',
        description: 'View your signing keys',
      },
      {
        key: 'signer.list.self',
        label: 'List signers',
        description: 'List your signing keys',
      },
      {
        key: 'signer.register.self',
        label: 'Register signers',
        description: 'Register new signing keys',
      },
      {
        key: 'signer.remove.self',
        label: 'Remove signers',
        description: 'Remove signing keys',
      },
      {
        key: 'logs.read',
        label: 'Read logs',
        description: 'View activity logs',
      },
      {
        key: 'logs.write',
        label: 'Write logs',
        description: 'Create log entries',
      },
    ],
  },
];

export const WORKFLOW_PRESETS: UseCasePreset[] = [
  {
    id: 'payments-server',
    label: 'Payment Processing',
    description: 'Server-side payment creation, listing, and management',
    icon: CreditCard,
    scopes: [
      'payments.write.self',
      'payments.write',
      'payments.read.self',
      'payments.read',
      'payments.create',
      'payments.list',
      'payments.pay',
      'accounts.read.self',
      'accounts.balance',
    ],
  },
  {
    id: 'agent-automation',
    label: 'Agent Automation',
    description: 'AI agent access to accounts, cards, chats, and transfers',
    icon: Bot,
    scopes: [
      'agent.read',
      'agent.write',
      'chat.read',
      'chat.write',
      'file.read',
      'file.write',
      'accounts.read.self',
      'accounts.balance',
      'accounts.transfer.self',
      'accounts.list_transactions',
      'mediums.read',
      'mediums.write.self',
      'identity.read.self',
      'compliance.read',
    ],
  },
  {
    id: 'account-operations',
    label: 'Account Operations',
    description: 'Balances, transactions, transfers, tokenization, and mediums',
    icon: Wallet,
    scopes: [
      'accounts.read',
      'accounts.read.self',
      'accounts.balance',
      'accounts.list',
      'accounts.list_transactions',
      'accounts.transfer',
      'accounts.transfer.self',
      'accounts.sync.self',
      'accounts.batch',
      'accounts.tokenize',
      'accounts.tokenize.self',
      'mediums.read',
      'mediums.write',
      'mediums.write.self',
      'mediums.update.self',
    ],
  },
  {
    id: 'market-making',
    label: 'Swaps & Trading',
    description:
      'Execute swaps, publish orders, rate calculation, and market making',
    icon: ArrowLeftRight,
    scopes: [
      'swap.take',
      'swap.taker',
      'swap.publish',
      'swap.write.self',
      'swap.read',
      'swap.read.self',
      'swap.list',
      'swap.maker',
      'swap.find_rate',
      'swap.calculate_rates',
      'swap.market_maker',
      'swap.orders.list',
      'graph.template.read',
      'graph.template.generate',
      'accounts.read.self',
      'accounts.balance',
    ],
  },
  {
    id: 'compliance-automation',
    label: 'Compliance Automation',
    description: 'Trigger KYC verification flows and read compliance status',
    icon: ShieldCheck,
    scopes: [
      'compliance.read',
      'compliance.read.documents',
      'compliance.write',
      'compliance.start_verification',
      'identity.read.self',
    ],
  },
  {
    id: 'identity-profiles',
    label: 'Identity & Profiles',
    description: 'Manage your profile, aliases, and identity verification',
    icon: UserCircle,
    scopes: [
      'identity.read.self',
      'identity.update.self',
      'alias.read.self',
      'alias.find',
      'alias.verify',
      'alias.write.self',
    ],
  },
  {
    id: 'org-management',
    label: 'Organization Management',
    description: 'Teams, members, roles, and invitations',
    icon: Building2,
    scopes: [
      'orgs.read',
      'orgs.read.self',
      'orgs.write',
      'orgs.write.self',
      'orgs.create',
      'orgs.find',
      'orgs.remove',
      'team.read',
      'team.write',
      'team.write.self',
      'teams.read.self',
      'teams.write.self',
      'member.read',
      'member.write',
      'member.write.self',
      'members.read.self',
      'members.write.self',
      'role.read',
      'role.read.self',
      'role.write',
      'role.write.self',
      'invite.accept',
      'invite.reject',
      'invite.write.self',
    ],
  },
  {
    id: 'security-keys',
    label: 'Security & Key Management',
    description: 'API keys, signing keys, and activity logs',
    icon: KeyRound,
    scopes: [
      'api-key.list',
      'api-key.validate',
      'api-key.revoke',
      'api-key.rotate',
      'signer.get.self',
      'signer.list.self',
      'signer.register.self',
      'signer.remove.self',
      'logs.read',
      'logs.write',
    ],
  },
];

const CUSTOM_PRESET: UseCasePreset = {
  id: 'custom',
  label: 'Custom',
  description: 'Start from scratch and pick exactly the scopes you need',
  icon: KeyRound,
  scopes: [],
};

export const USE_CASE_PRESETS: UseCasePreset[] = [
  ...WORKFLOW_PRESETS,
  CUSTOM_PRESET,
];

const ALL_SCOPE_KEYS = new Set(
  SCOPE_GROUPS.flatMap((g) => g.scopes.map((s) => s.key)),
);

export function validateScopes(scopes: string[]): {
  valid: string[];
  invalid: string[];
} {
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const s of scopes) {
    if (ALL_SCOPE_KEYS.has(s)) valid.push(s);
    else invalid.push(s);
  }
  return { valid, invalid };
}

export function buildScopesFromPreset(
  presetId: string,
  manualOverrides?: string[],
): string[] {
  const preset = USE_CASE_PRESETS.find((p) => p.id === presetId);
  const base = preset?.scopes ?? [];
  if (!manualOverrides) return [...base];
  const merged = new Set([...base, ...manualOverrides]);
  const { valid } = validateScopes([...merged]);
  return valid;
}

export function findScopeEntry(key: string): ScopeEntry | undefined {
  for (const group of SCOPE_GROUPS) {
    const entry = group.scopes.find((s) => s.key === key);
    if (entry) return entry;
  }
  return undefined;
}

export function filterScopeGroups(
  groups: ScopeGroup[],
  allowedScopes: Set<string> | null,
): ScopeGroup[] {
  if (!allowedScopes) return groups;
  return groups
    .map((group) => ({
      ...group,
      scopes: group.scopes.filter((s) => allowedScopes.has(s.key)),
    }))
    .filter((group) => group.scopes.length > 0);
}

export function filterPresets(
  presets: UseCasePreset[],
  allowedScopes: Set<string> | null,
): UseCasePreset[] {
  if (!allowedScopes) return presets;
  return presets
    .map((preset) => ({
      ...preset,
      scopes: preset.scopes.filter((s) => allowedScopes.has(s)),
    }))
    .filter((preset) => preset.id === 'custom' || preset.scopes.length > 0);
}
